import { create } from "zustand"
import JSZip from "jszip"
import { requestJSON } from "./request-module"
import semver from "semver"

const useStore = create((set, get) => ({
  compiling: false,
  setCompiling: (compiling) => set({ compiling }),
  solidityCompilers: [],
  setSolidityCompilers: (compilers) => set({ solidityCompilers: compilers }),
  versionInfo: null,
  setVersionInfo: (info) => set({ versionInfo: info }),
  mainContract: null,
  setMainContract: (name) => set({ mainContract: name }),
  mainFile: null,
  setMainFile: (name) => set({ mainFile: name }),
  allContracts: [],
  setAllContracts: (contracts) => set({ allContracts: contracts }),
  uploadedSolFiles: [],
  setUploadedSolFiles: (files) => set({ uploadedSolFiles: files }),
  myReviews: [],
  setMyReviews: (reviews) => set({ myReviews: reviews }),
  lastCreatedReviewId: null,
  setLastCreatedReviewId: (id) => set({ lastCreatedReviewId: id }),
  compilationPayload: null,
  setCompilationPayload: (payload) => set({ compilationPayload: payload }),
  oneReview: null,
  setOneReview: (review) => set({ oneReview: review }),
  completedTasks: [],
  setCompletedTasks: (tasks) => set({ completedTasks: tasks }),
  simulations: [],
  setSimulations: (simulations) => set({ simulations }),
  showSimulationNameInputModal: false,
  simulationNameInputModalConfig: { operation: "create" },
  setSimulationNameInputModal: (show, config = { operation: "create" }) => {
    set({
      showSimulationNameInputModal: show,
      simulationNameInputModalConfig: show ? config : { operation: "create" },
    })
  },
  filteredSimulations: [],
  setFilteredSimulations: (filtered) => set({ filteredSimulations: filtered }),

  // Simulation-specific state
  selectedSimulationId: null,
  setSelectedSimulationId: (id) => set({ selectedSimulationId: id }),

  selectedSimulation: null,
  setSelectedSimulation: (simulation) =>
    set({ selectedSimulation: simulation }),

  isSimulationDetailView: false,
  setSimulationDetailView: (isDetail) =>
    set({ isSimulationDetailView: isDetail }),

  isReadOnly: false,
  isSaving: false,
  saved: true,
  isRunning: false,

  // Toggle between list and detail views
  showSimulationDetail: (simulationId) => {
    set({
      selectedSimulationId: simulationId,
      isSimulationDetailView: true,
      selectedSimulation: null, // Reset to trigger loading
    })
    // Fetch the simulation data
    get().fetchSimulation(simulationId)
    // Add review_id to URL
    const { oneReview } = get()
    if (oneReview && oneReview._id) {
      // Update URL with review_id parameter
      const url = new URL(window.location.href)
      url.searchParams.set("review_id", oneReview._id)
      window.history.pushState({}, "", url)
    }
  },

  // Go back to simulation list
  showSimulationsList: () => {
    set({
      isSimulationDetailView: false,
      selectedSimulationId: null,
      selectedSimulation: null,
    })
  },

  // Fetch a specific simulation's data
  fetchSimulation: async (simulationId) => {
    try {
      set({ isSaving: false, saved: true }) // Reset saved state

      const response = await requestJSON(
        "GET",
        `/simulation/${simulationId}`,
        null,
        false,
      )

      if (response) {
        set({ selectedSimulation: response })
        return response
      } else {
        throw new Error("Failed to fetch simulation")
      }
    } catch (error) {
      console.error("Error fetching simulation:", error)
      return null
    }
  },

  // Save simulation changes
  saveSimulation: async (simulationId) => {
    const simulation = get().selectedSimulation
    if (!simulation) return

    try {
      set({ isSaving: true, saved: false })

      // Get only the parts we want to update
      const updatePayload = {
        steps: simulation.steps,
        results: simulation.results,
      }

      const response = await requestJSON(
        "PUT",
        `/v2/simulation/${simulationId}`,
        updatePayload,
        false,
      )

      if (response && response.success) {
        set({ saved: true })
        return response
      }
    } catch (error) {
      console.error("Error saving simulation:", error)
    } finally {
      set({ isSaving: false })
    }
  },

  // Update a simulation property
  updateSimulationProperty: async (simulationId, property, value) => {
    try {
      set({ isSaving: true })

      const response = await requestJSON(
        "PUT",
        `/simulation/${simulationId}/property`,
        { property, value },
        false,
      )

      if (response && response.success) {
        // Update local state too
        set((state) => ({
          selectedSimulation: state.selectedSimulation
            ? {
                ...state.selectedSimulation,
                [property]: value,
              }
            : null,
        }))
        return response
      }
    } catch (error) {
      console.error("Error updating simulation property:", error)
    } finally {
      set({ isSaving: false })
    }
  },

  // Run a simulation
  runSimulation: async (simulationId) => {
    try {
      set({ isRunning: true })

      const response = await requestJSON(
        "POST",
        `/v2/simulation/${simulationId}/run`,
        null,
        false,
      )

      if (response) {
        // Update the simulation with new results
        set((state) => ({
          selectedSimulation: state.selectedSimulation
            ? {
                ...state.selectedSimulation,
                results: response.results,
              }
            : null,
        }))
        return response
      }
    } catch (error) {
      console.error("Error running simulation:", error)
      throw error
    } finally {
      set({ isRunning: false })
    }
  },

  // Duplicate a simulation
  duplicateSimulation: async (sourceId, newName) => {
    try {
      const response = await requestJSON(
        "PUT",
        `/v2/simulation/${sourceId}/duplicate`,
        { name: newName, sourceId: sourceId },
        false,
      )

      if (response) {
        return response
      }
    } catch (error) {
      console.error("Error duplicating simulation:", error)
      throw error
    }
  },

  containsSolFiles: (files) => {
    for (const file of files) {
      if (file.name.endsWith(".sol")) {
        return true
      }
    }
    return false
  },
  unzipFiles: async (zipFile) => {
    const zip = await JSZip.loadAsync(zipFile)
    const files = []
    await Promise.all(
      Object.keys(zip.files).map(async (filename) => {
        if (filename.startsWith("__MACOSX/")) return
        const entry = zip.files[filename]
        if (!entry.dir) {
          const fileData = await entry.async("blob")
          const parts = filename.split("/")
          const cleanName =
            parts.length > 1 ? parts.slice(1).join("/") : filename
          files.push({
            name: cleanName,
            file: fileData,
          })
        }
      }),
    )
    return files
  },
  fetchSolidityCompilers: async (showAlert) => {
    try {
      const response = await requestJSON(
        "GET",
        "/get_solidity_compilers",
        null,
        false,
        showAlert,
      )
      if (response) {
        get().setSolidityCompilers(response.solidityCompilers || [])
      }
    } catch (error) {
      if (showAlert) {
        showAlert({
          message: "Failed to fetch Solidity compilers.",
          severity: "error",
          title: "Error",
        })
      }
      console.error("Failed to fetch Solidity compilers:", error)
    }
  },
  analyzeSolidityVersions: async (files) => {
    const extractPragmaConstraints = (content) => {
      // Strip line and block comments so commented-out pragmas are ignored
      const stripped = content
        .replace(/\/\*[\s\S]*?\*\//g, "")
        .replace(/\/\/[^\n\r]*/g, "")
      // Match every pragma in the file (a file may have multiple)
      const constraints = []
      const re = /pragma\s+solidity\s+([^;]+);/gi
      let m
      while ((m = re.exec(stripped)) !== null) {
        const parts = m[1]
          .split(/\s+/)
          .map((s) =>
            s
              .trim()
              .replace(/^"(.*)"$/, "$1")
              .replace(/^'(.*)'$/, "$1"),
          )
          .filter(Boolean)
        if (parts.length > 0) constraints.push(parts)
      }
      return constraints
    }

    let allConstraints = []
    for (const fileObj of files) {
      let content = ""
      if (fileObj.file && fileObj.file.text) {
        content = await fileObj.file.text()
      } else if (fileObj.text) {
        content = await fileObj.text()
      } else if (typeof fileObj.content === "string") {
        content = fileObj.content
      }
      const constraints = extractPragmaConstraints(content)
      if (constraints.length > 0) allConstraints.push(...constraints)
    }

    if (allConstraints.length === 0) {
      return { minVersion: null, maxVersion: null, maxUsableVersion: null }
    }

    const flatConstraints = [].concat.apply([], allConstraints)
    const solidityToSemver = (constraint) => {
      // Handle quoted versions by removing quotes
      const cleanConstraint = constraint
        .replace(/^"(.*)"$/, "$1")
        .replace(/^'(.*)'$/, "$1")
        .trim()

      if (!cleanConstraint) return ""

      // If it's just a bare version without any operator, treat as exact match
      if (/^\d+\.\d+\.\d+$/.test(cleanConstraint)) {
        return `=${cleanConstraint}`
      }

      // Pass ^, ~, >=, <=, >, <, = prefixed constraints directly to the semver
      // library. It already handles them correctly and matches Solidity semantics:
      //   ^0.8.20  → >=0.8.20 <0.9.0  (NOT <1.0.0 — 0.x caret pins the minor)
      //   ^0.4.24  → >=0.4.24 <0.5.0
      //   ~0.8.20  → >=0.8.20 <0.9.0
      //   >=0.8.0  → >=0.8.0
      //   <0.9.0   → <0.9.0
      return cleanConstraint
    }

    const semverRange = flatConstraints
      .map(solidityToSemver)
      .filter(Boolean)
      .join(" ")
    let solidityCompilers = get().solidityCompilers
    if (!solidityCompilers || solidityCompilers.length === 0) {
      await get().fetchSolidityCompilers()
      solidityCompilers = get().solidityCompilers
    }

    // Normalize a compiler list entry (e.g. "v0.8.20+commit.a1b79de6") into a semver-comparable string.
    const toSemver = (v) => {
      if (!v) return null
      const cleaned = String(v).replace(/^v/, "").split("+")[0]
      const parsed = semver.valid(cleaned, { loose: true })
      return parsed
    }

    // Only consider stable releases — skip nightly / pre-release builds (e.g. 0.8.35-pre.1)
    // because the Solidity compiler binaries list often contains pre-release entries that
    // would otherwise win the "max usable version" race for a pragma like ^0.8.20.
    const isStable = (parsedVersion) =>
      parsedVersion && !semver.prerelease(parsedVersion)

    const usableVersions = (solidityCompilers || [])
      .map((raw) => ({ raw, parsed: toSemver(raw) }))
      .filter(({ parsed }) => isStable(parsed))
      .filter(({ parsed }) => {
        try {
          return semver.satisfies(parsed, semverRange)
        } catch (err) {
          console.error(
            `Error comparing version ${parsed} with range ${semverRange}:`,
            err,
          )
          return false
        }
      })

    const allVersions = flatConstraints
      .map((c) => {
        // Remove quotes if present and extract version
        const cleaned = c.replace(/^"(.*)"$/, "$1").replace(/^'(.*)'$/, "$1")
        const m = cleaned.match(/(\d+\.\d+\.\d+)/)
        return m ? m[1] : null
      })
      .filter(Boolean)
      .sort(semver.compare)

    const minVersion = allVersions[0] || null
    const maxVersion = allVersions[allVersions.length - 1] || null

    let maxUsableVersion = null
    if (usableVersions.length > 0) {
      usableVersions.sort((a, b) => semver.compare(a.parsed, b.parsed))
      // Keep the original string (e.g. "v0.8.26+commit.8a97fa7a") because the
      // backend compiler loader expects that exact format.
      maxUsableVersion = usableVersions[usableVersions.length - 1].raw
    }

    return { minVersion, maxVersion, maxUsableVersion }
  },

  getAllContracts: async (files) => {
    // Match `contract Foo` and `abstract contract Foo` anywhere on a line.
    // Strip comments first so commented-out declarations are ignored.
    const contractRegex =
      /(?:^|[;{}\s])(?:abstract\s+)?contract\s+([A-Za-z_][A-Za-z0-9_]*)\b/g
    const contractSet = new Set()

    for (const file of files) {
      const name = file.name || file.path
      if (!name || !name.endsWith(".sol")) continue

      let content = ""
      if (file.file && typeof file.file.text === "function") {
        content = await file.file.text()
      } else if (typeof file.text === "function") {
        content = await file.text()
      } else if (typeof file.content === "string") {
        content = file.content
      } else if (typeof file.text === "string") {
        content = file.text
      } else if (file instanceof Blob) {
        content = await file.text()
      }

      const stripped = content
        .replace(/\/\*[\s\S]*?\*\//g, "")
        .replace(/\/\/[^\n\r]*/g, "")

      let match
      while ((match = contractRegex.exec(stripped)) !== null) {
        contractSet.add(match[1])
      }
    }
    return Array.from(contractSet)
  },
  findMainContractAndMainFile: async (files, showAlert) => {
    try {
      const filesWithContent = await Promise.all(
        files.map(async (f) => {
          let content = ""
          if (f.file && f.file.text) {
            content = await f.file.text()
          } else if (f.text) {
            content = await f.text()
          }
          return { name: f.name, content }
        }),
      )

      const response = await requestJSON(
        "POST",
        "/compilation/find_main_contract_and_main_file",
        { files: filesWithContent },
        false,
        showAlert,
      )
      if (response && response.mainContract && response.mainFile) {
        return response
      } else {
        if (showAlert) {
          showAlert({
            message:
              response?.message ||
              "Could not determine main contract or main file.",
            severity: "error",
            title: "Error",
          })
        }
        return null
      }
    } catch (err) {
      if (showAlert) {
        showAlert({
          message: "Failed to find main contract or main file.",
          severity: "error",
          title: "Error",
        })
      }
      return null
    }
  },
  getCompiled: async (payload, showAlert) => {
    try {
      const response = await requestJSON(
        "POST",
        "/get-compiled",
        payload,
        false,
        showAlert,
      )
      if (!response) {
        if (showAlert) {
          showAlert({
            message: "Failed to compile contracts.",
            severity: "error",
            title: "Compilation Error",
          })
        }
        return null
      }
      return response
    } catch (err) {
      if (showAlert) {
        showAlert({
          message:
            err?.message || "Compilation failed due to an unexpected error.",
          severity: "error",
          title: "Compilation Error",
        })
      }
      return null
    }
  },
  runTests: async (payload, showAlert) => {
    try {
      const response = await requestJSON(
        "POST",
        "/compilation/run_tests",
        payload,
        false,
        showAlert,
      )
      if (!response) {
        if (showAlert) {
          showAlert({
            message: "Failed to run tests on contracts.",
            severity: "error",
            title: "Testing Error",
          })
        }
        return null
      }
      return response
    } catch (err) {
      if (showAlert) {
        showAlert({
          message: err?.message || "Tests failed due to an unexpected error.",
          severity: "error",
          title: "Testing Error",
        })
      }
      console.error("Error running tests:", err)
      return null
    }
  },
  submitTestDetails: async (formData, showAlert) => {
    try {
      const response = await requestJSON(
        "POST",
        "/compilation/submit_test_details",
        formData,
        false,
      )
      if (response && response.success && response.reviewId) {
        get().setLastCreatedReviewId(response.reviewId)
      }
      return response
    } catch (err) {
      if (showAlert) {
        showAlert({
          message: "Failed to find main contract.",
          severity: "error",
          title: "Error",
        })
      }
      return null
    }
  },
  analyze: async (payload, showAlert) => {
    try {
      const response = await requestJSON(
        "POST",
        "/sc_analyzer/analyze",
        payload,
        false,
      )
      return response
    } catch (err) {
      if (showAlert) {
        showAlert({
          message: "Failed to analyze contract.",
          severity: "error",
          title: "Error",
        })
      }
      return null
    }
  },
  generateAiBasedSimulations: async (reviewId, showAlert) => {
    try {
      const response = await requestJSON(
        "POST",
        "/v2/simulation/generate-ai-based-simulations",
        { reviewId: reviewId },
        false,
      )
      return response
    } catch (err) {
      if (showAlert) {
        showAlert({
          message: err?.message || "Failed to generate AI-based simulations",
          severity: "error",
          title: "AI Simulation Error",
        })
      }
      return null
    }
  },
  runTestRunner: async (compilationPayload, showAlert) => {
    try {
      const response = await requestJSON(
        "POST",
        "/compilation/test_runner",
        { compilationPayload },
        false,
        showAlert,
      )
      if (response && response.success) {
        // Store the reviewId immediately so polling can start
        if (response.reviewId) {
          set({ lastCreatedReviewId: response.reviewId })
        }
        return response
      } else {
        return { error: response?.message || "Test runner failed" }
      }
    } catch (err) {
      if (showAlert) {
        showAlert({
          message: err?.message || "Test runner failed",
          severity: "error",
          title: "Test Runner Error",
        })
      }
      return { error: err?.message || "Test runner failed" }
    }
  },
  getAiSimulationStatus: async (reviewId) => {
    try {
      const response = await requestJSON(
        "GET",
        `/v2/simulation/ai-status/${reviewId}`,
        null,
        false,
      )
      return response || {}
    } catch (err) {
      console.error("Error getting AI simulation status:", err)
      return {}
    }
  },
  getCompilationStatus: async (reviewId) => {
    try {
      console.log(`Fetching compilation status for reviewId: ${reviewId}`)
      const response = await requestJSON(
        "GET",
        `/compilation/compilation_status/${reviewId}`,
        null,
        false,
      )
      console.log(`Compilation status response:`, response)
      return response || {}
    } catch (err) {
      console.error("Error getting compilation status:", err)
      return {}
    }
  },
  fetchMyReviews: async () => {
    try {
      const response = await requestJSON(
        "GET",
        "/review/my-reviews-v2",
        null,
        false,
      )
      if (response && Array.isArray(response)) {
        get().setMyReviews(response)
      }
    } catch (err) {
      get().setMyReviews([])
    }
  },
  askAI: async (payload, showAlert) => {
    try {
      const response = await requestJSON(
        "POST",
        "/review/get-chat-response-from-system",
        payload,
        false,
      )
      return response
    } catch (err) {
      if (showAlert) {
        showAlert({
          message: "Failed to get response from AI.",
          severity: "error",
          title: "Error",
        })
      }
      return null
    }
  },
  submitAIfeedback: async (payload, showAlert) => {
    try {
      const response = await requestJSON("POST", "/feedback/ai", payload, false)
      return response
    } catch (err) {
      if (showAlert) {
        showAlert({
          message: "Failed to submit AI feedback.",
          severity: "error",
          title: "Error",
        })
      }
      return null
    }
  },
  fetchOneReview: async (reviewId) => {
    try {
      const response = await requestJSON(
        "GET",
        `/review/my-review/${reviewId}`,
        null,
        false,
      )
      if (response) {
        // Create tabsObject from tabsArray
        if (
          response.analyzedData &&
          response.analyzedData.json &&
          Array.isArray(response.analyzedData.json.tabsArray)
        ) {
          // Ensure tabsObject exists
          if (!response.analyzedData.json.tabsObject) {
            response.analyzedData.json.tabsObject = {}
          }
          response.analyzedData.json.tabsArray.forEach((item) => {
            response.analyzedData.json.tabsObject[item.id] = item
          })
        }
        get().setOneReview(response)
      }
    } catch (err) {
      get().setOneReview(null)
    }
  },
  updateReview: async (fieldName, value, reviewId, showAlert) => {
    try {
      const response = await requestJSON(
        "PUT",
        `/review/my-review/${reviewId}`,
        {
          [fieldName]: value,
        },
      )
      if (response && response.success) {
        get().fetchMyReviews()
      }
      if (showAlert) {
        showAlert({
          message: response.message || `Review updated successfully`,
          severity: "success",
          title: "Success",
        })
      }
    } catch (err) {
      if (showAlert) {
        showAlert({
          message: err.message || "Failed to update review",
          severity: "error",
          title: "Error",
        })
      }
      console.error("Error updating review:", err)
      return null
    }
  },
  getTasks: async (id) => {
    const taskReviewId = id || "50a"
    try {
      const response = await requestJSON(
        "POST",
        `/get-tasks?taskReviewId=${taskReviewId}`,
      )
      if (response && response.completedTasks) {
        get().setCompletedTasks(response.completedTasks)
      }
    } catch (err) {
      console.error("Error fetching tasks:", err)
    }
  },
  saveTasks: async (id) => {
    const taskReviewId = id || "50a"
    try {
      const completedTasks = get().completedTasks
      await requestJSON(
        "POST",
        `/save-tasks?taskReviewId=${taskReviewId}`,
        completedTasks,
      )
    } catch (err) {
      console.error("Error saving tasks:", err)
    }
  },
  fetchSimulations: async (reviewId) => {
    // Clear any previously loaded simulations so stale data from a different
    // review is not shown while the new list is being fetched (or when the
    // new review has no simulations at all). Also reset the detail view so a
    // simulation opened from a previous review is not displayed when the
    // Simulator tab is opened for a different review.
    set({
      simulations: [],
      filteredSimulations: [],
      isSimulationDetailView: false,
      selectedSimulationId: null,
      selectedSimulation: null,
    })
    try {
      const response = await requestJSON(
        "GET",
        `/v2/simulations-metadata?reviewId=${reviewId}`,
        null,
        false,
      )
      if (response) {
        get().setSimulations(response)
        get().setFilteredSimulations(response)
      }
    } catch (err) {
      console.error("Error fetching simulations:", err)
      get().setSimulations([])
      get().setFilteredSimulations([])
    }
  },
  deleteReviews: async (reviewIds, showAlert) => {
    try {
      if (!Array.isArray(reviewIds) || reviewIds.length === 0) {
        throw new Error("Review IDs must be a non-empty array")
      }

      const response = await requestJSON(
        "DELETE",
        "/review/my-reviews-v2",
        { reviewIds },
        false,
        showAlert,
      )

      if (response && response.success) {
        // Refresh the reviews list after successful deletion
        await get().fetchMyReviews()

        // Show success alert if callback provided
        if (showAlert) {
          showAlert({
            message:
              response.message ||
              `Successfully deleted ${response.deletedCount} review(s)`,
            severity: "success",
            title: "Success",
          })
        }

        return response
      } else {
        throw new Error(response?.error || "Failed to delete reviews")
      }
    } catch (err) {
      if (showAlert) {
        showAlert({
          message: err.message || "Failed to delete reviews",
          severity: "error",
          title: "Error",
        })
      }
      console.error("Error deleting reviews:", err)
      return null
    }
  },
  // Add this function to your store
  downloadDocument: async (docId, fileName, showAlert) => {
    try {
      if (!docId) {
        throw new Error("Document ID is required")
      }

      // Use requestJSON to handle authentication, error handling etc.
      // But pass 'raw' option to get raw response instead of parsed JSON
      const response = await requestJSON(
        "GET",
        `/review/document/${docId}`,
        null,
        false, // Don't use cache
        showAlert,
        true, // Return raw response
      )

      // Check if the response is null (which means requestJSON already handled an error)
      if (!response) {
        throw new Error(
          "Failed to download document - server returned an error",
        )
      }

      // Now we can safely check response.ok
      if (!response.ok) {
        throw new Error(
          `Failed to download document: ${response.status} ${response.statusText}`,
        )
      }

      // Get the document as blob - this preserves binary data properly
      const blob = await response.blob()

      // Create a download link
      const downloadUrl = window.URL.createObjectURL(blob)

      // Extract filename from Content-Disposition header if available
      let downloadFileName = fileName
      const contentDisposition = response.headers.get("Content-Disposition")
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/i)
        if (fileNameMatch && fileNameMatch[1]) {
          downloadFileName = decodeURIComponent(fileNameMatch[1])
        }
      }

      // Create a temporary anchor element to trigger download
      const a = document.createElement("a")
      a.style.display = "none"
      a.href = downloadUrl
      a.download = downloadFileName || "document"
      document.body.appendChild(a)
      a.click()

      // Clean up
      window.URL.revokeObjectURL(downloadUrl)
      document.body.removeChild(a)

      return true
    } catch (err) {
      console.error("Error downloading document:", err)
      if (showAlert) {
        showAlert({
          message: err.message || "Failed to download document",
          severity: "error",
          title: "Download Error",
        })
      }
      return false
    }
  },
  regenerateFormalSpecification: async (reviewId, showAlert) => {
    try {
      if (!reviewId) {
        throw new Error("Review ID is required")
      }

      const response = await requestJSON(
        "POST",
        `/review/regenerate-specification/${reviewId}`,
        null,
        false,
        showAlert,
      )

      if (response && response.success) {
        // Refresh the review data to get the newly generated specification
        await get().fetchOneReview(reviewId)

        if (showAlert) {
          showAlert({
            message:
              response.message ||
              "Formal specification regenerated successfully",
            severity: "success",
            title: "Success",
          })
        }

        return true
      } else {
        throw new Error(
          response?.error || "Failed to regenerate formal specification",
        )
      }
    } catch (err) {
      console.error("Error regenerating formal specification:", err)
      if (showAlert) {
        showAlert({
          message: err.message || "Failed to regenerate formal specification",
          severity: "error",
          title: "Error",
        })
      }
      return false
    }
  },
  createNewSimulation: async (simulationParams, showAlert) => {
    try {
      const response = await requestJSON(
        "POST",
        "/v2/simulation",
        simulationParams,
        false,
        showAlert,
      )

      if (response) {
        await get().fetchSimulations(simulationParams.review_id)

        if (showAlert) {
          showAlert({
            message: `Simulation created successfully`,
            severity: "success",
            title: "Success",
          })
        }

        return response
      } else {
        throw new Error(response?.error || "Failed to create simulation")
      }
    } catch (err) {
      console.error("Error creating simulation:", err)

      if (showAlert) {
        showAlert({
          message: err.message || "Failed to create simulation",
          severity: "error",
          title: "Error",
        })
      }

      return null
    }
  },
  createSimulationWithAI: async (simulationParams, showAlert) => {
    try {
      const response = await requestJSON(
        "POST",
        "/v2/simulation-ai",
        simulationParams,
        false,
        showAlert,
      )

      if (response) {
        await get().fetchSimulations(simulationParams.review_id)

        if (showAlert) {
          showAlert({
            message: `Simulation created successfully`,
            severity: "success",
            title: "Success",
          })
        }

        return response
      } else {
        throw new Error(response?.error || "Failed to create simulation")
      }
    } catch (err) {
      console.error("Error creating simulation:", err)

      if (showAlert) {
        showAlert({
          message: err.message || "Failed to create simulation",
          severity: "error",
          title: "Error",
        })
      }

      return null
    }
  },
  updateSimulation: async (fieldName, value, simulationId, showAlert) => {
    try {
      const response = await requestJSON("PUT", `/simulation/${simulationId}`, {
        [fieldName]: value,
      })
      if (showAlert) {
        showAlert({
          message: response.message || `Simulation updated successfully`,
          severity: "success",
          title: "Success",
        })
      }
    } catch (err) {
      if (showAlert) {
        showAlert({
          message: err.message || "Failed to update simulation",
          severity: "error",
          title: "Error",
        })
      }
      console.error("Error updating simulation:", err)
      return null
    }
  },
  deleteSimulation: async (simulationIds, showAlert) => {
    try {
      if (!Array.isArray(simulationIds) || simulationIds.length === 0) {
        throw new Error("Simulation IDs must be a non-empty array")
      }

      const response = await requestJSON(
        "DELETE",
        "/v2/simulations",
        { simulationIds },
        false,
        showAlert,
      )

      if (response && response.success) {
        // Update local state by filtering out deleted simulations
        set((state) => ({
          simulations: state.simulations.filter(
            (sim) => !simulationIds.includes(sim._id),
          ),
          // Also update filtered simulations to maintain UI consistency
          filteredSimulations: state.filteredSimulations.filter(
            (sim) => !simulationIds.includes(sim._id),
          ),
        }))

        // Show success alert if callback provided
        if (showAlert) {
          showAlert({
            message:
              response.message ||
              `Successfully deleted ${response.deletedCount} simulation(s)`,
            severity: "success",
            title: "Success",
          })
        }

        return response
      } else {
        throw new Error(response?.error || "Failed to delete simulations")
      }
    } catch (err) {
      if (showAlert) {
        showAlert({
          message: err.message || "Failed to delete simulations",
          severity: "error",
          title: "Error",
        })
      }
      console.error("Error deleting simulations:", err)
      return null
    }
  },

  saveReportsToReview: async (reports, reviewId, showAlert) => {
    try {
      const response = await requestJSON(
        "POST",
        "/compilation/save_reports_to_review",
        { reports, reviewId },
        false,
        showAlert,
      )

      if (response && response.success) {
        return response
      } else {
        throw new Error(response?.error || "Failed to save reports")
      }
    } catch (err) {
      console.error("Error saving reports:", err)

      if (showAlert) {
        showAlert({
          message: err.message || "Failed to save reports to review",
          severity: "error",
          title: "Error",
        })
      }

      return null
    }
  },
// In store.js, update the reRunTests function:

  reRunTests: async (reviewId, showAlert) => {
    try {
      // First ensure we have the review data
      let review = get().oneReview
      if (!review || review._id !== reviewId) {
        await get().fetchOneReview(reviewId)
        review = get().oneReview
      }

      if (!review || !review.files || !Array.isArray(review.files)) {
        throw new Error("Review files not found")
      }

      // Step 1: Generate AI-based simulations (similar to compilation-steps)
      try {
        console.log("Generating AI-based simulations for review:", reviewId)
        const aiSimulationsRes = await get().generateAiBasedSimulations(reviewId, showAlert)
        
        if (!aiSimulationsRes || aiSimulationsRes.error) {
          console.warn("Warning: Failed to generate AI-based simulations", aiSimulationsRes)
          // Continue anyway since this is non-critical
        } else if (aiSimulationsRes.skipped) {
          console.log("AI-based simulations generation was skipped due to backend configuration")
        } else {
          console.log("Successfully initiated AI-based simulations generation")
        }
        
        // Add a small delay to allow AI generation to process (only if not skipped)
        if (!aiSimulationsRes?.skipped) {
          await new Promise((res) => setTimeout(res, 1500))
        }
      } catch (err) {
        console.warn("Warning: Error generating AI-based simulations", err)
        // Continue anyway since this is non-critical
        await new Promise((res) => setTimeout(res, 500))
      }

      // Step 2: Run tests on the files
      const payload = {
        review_id: reviewId,
        files: review.files,
      }

      console.log("Running tests for review:", reviewId)
      const testResults = await get().runTests(payload, showAlert)

      if (!testResults) {
        throw new Error("Failed to run tests")
      }

      console.log("Tests completed successfully")
      
      // Return the raw test results for the component to process
      return testResults
    } catch (err) {
      console.error("Error re-running tests:", err)
      if (showAlert) {
        showAlert({
          message: err.message || "Failed to re-run tests",
          severity: "error",
          title: "Testing Error",
        })
      }
      return null
    }
  },
  reRunAllSimulations: async (reviewId, showAlert) => {
    try {
      const { simulations } = get()
      
      if (!simulations || simulations.length === 0) {
        // Fetch simulations first if not available
        await get().fetchSimulations(reviewId)
        const updatedSimulations = get().simulations
        
        if (!updatedSimulations || updatedSimulations.length === 0) {
          throw new Error("No simulations found to re-run")
        }
      }

      const currentSimulations = get().simulations

      // Re-run each simulation
      console.log(`Re-running ${currentSimulations.length} simulations...`)
      
      for (const sim of currentSimulations) {
        try {
          console.log(`Running simulation: ${sim.name || sim._id}`)
          
          const response = await requestJSON(
            "POST", 
            "/custom-test", 
            {
              storyId: sim._id,
              reviewId: reviewId,
            },
            false
          )
          
          console.log(`Simulation ${sim.name || sim._id} result:`, {
            pass_expections: response.pass_expections,
            success: response.success,
            error: response.error
          })
          
          // Update local simulation state with new results and full response
          set((state) => ({
            simulations: state.simulations.map(s => 
              s._id === sim._id 
                ? { 
                    ...s, 
                    passExpections: response.pass_expections,
                    results: response ? [response] : s.results
                  }
                : s
            ),
            filteredSimulations: state.filteredSimulations.map(s => 
              s._id === sim._id 
                ? { 
                    ...s, 
                    passExpections: response.pass_expections,
                    results: response ? [response] : s.results
                  }
                : s
            )
          }))
          
          // Small delay between requests to avoid overwhelming the server
          await new Promise(resolve => setTimeout(resolve, 200))
        } catch (err) {
          console.error(`Error re-running simulation ${sim._id}:`, err)
          // Continue with other simulations even if one fails
        }
      }

      return { success: true }
    } catch (err) {
      console.error("Error re-running all simulations:", err)
      if (showAlert) {
        showAlert({
          message: err.message || "Failed to re-run all simulations",
          severity: "error",
          title: "Error",
        })
      }
      return { success: false, error: err.message }
    }
  },

  generateSimulationFailureReports: async (reviewId, showAlert) => {
    try {
      const response = await requestJSON(
        "POST",
        "/compilation/generate_simulation_failure_reports",
        { reviewId },
        false,
        showAlert,
      )

      if (response && response.success) {
        return response
      } else {
        throw new Error(response?.error || "Failed to generate simulation failure reports")
      }
    } catch (err) {
      console.error("Error generating simulation failure reports:", err)

      if (showAlert) {
        showAlert({
          message: err.message || "Failed to generate simulation failure reports",
          severity: "error",
          title: "Error",
        })
      }

      return null
    }
  },

  // Fire-and-forget audit log. Errors here must never surface to the user
  // (Logging Policy §2.5.7 — a log-write failure must not break a request).
  createLog: async (data) => {
    try {
      await requestJSON("POST", "/logger/create-log", data || {}, false)
    } catch (err) {
      console.warn("createLog failed:", err && err.message)
    }
  },

  // Admin flag used to gate admin-only UI (e.g. the Audit Log tab in Reports).
  isAdmin: null,
  setIsAdmin: (val) => set({ isAdmin: val }),
  checkIsAdmin: async () => {
    try {
      const response = await requestJSON(
        "GET",
        "/organization/checkIsAdmin",
        null,
        false,
      )
      const isAdmin = Boolean(response)
      set({ isAdmin })
      return isAdmin
    } catch (err) {
      console.warn("checkIsAdmin failed:", err && err.message)
      set({ isAdmin: false })
      return false
    }
  },

  // Audit logs (admin-only). `fetchAuditLogs` takes ISO date strings
  // (YYYY-MM-DD) as accepted by the backend `/logger/logs/:start/:end` route.
  auditLogs: [],
  auditLogsLoading: false,
  setAuditLogs: (logs) => set({ auditLogs: logs || [] }),
  fetchAuditLogs: async (startDate, endDate, showAlert) => {
    try {
      set({ auditLogsLoading: true })
      const response = await requestJSON(
        "GET",
        `/logger/logs/${encodeURIComponent(startDate)}/${encodeURIComponent(endDate)}`,
        null,
        false,
        showAlert,
      )
      const logs = Array.isArray(response) ? response : []
      set({ auditLogs: logs })
      return logs
    } catch (err) {
      console.error("Error fetching audit logs:", err)
      set({ auditLogs: [] })
      if (showAlert) {
        showAlert({
          message: err.message || "Failed to fetch audit logs",
          severity: "error",
          title: "Error",
        })
      }
      return []
    } finally {
      set({ auditLogsLoading: false })
    }
  },
}))

export default useStore
